function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState, useRef, useEffect } from "react";
import Modal from "../../Modal/Modal";
import Chevron from "../../Chevron/Chevron";
import List from "../../List/List";
import { useGlobalValue } from "../../../state/GlobalProvider";
import styles from "./Dropdown.module.sass";
import Icon from "../../Icon/Icon";
const getRectStyle = elem => {
  const rect = elem && elem.getBoundingClientRect(elem);
  const drawRect = rect ? {
    left: rect.left,
    top: rect.bottom,
    maxHeight: window.innerHeight - Math.max(window.innerHeight * 0.05, rect.top + rect.height) - window.innerHeight * 0.05,
    minWidth: rect.width
  } : null;
  if (drawRect && drawRect.maxHeight < 200) {
    drawRect.top = rect.bottom;
    drawRect.maxHeight = 200;
  }
  return [drawRect, rect];
};
const scrollIntoViewIfNeeded = (selected, listContainer) => {
  if (selected >= 0 && listContainer.current) {
    const selectedItem = listContainer.current.querySelector(`.${styles["selected"]}`);
    if (selectedItem) {
      listContainer.current.scrollTo({
        top: selectedItem.offsetTop - listContainer.current.offsetHeight / 2,
        left: 0,
        behavior: "smooth"
      });
    }
  }
};
const Item = ({
  data
}) => {
  const {
    index,
    name,
    onClick,
    children,
    parent,
    selected
  } = data;
  const Tag = children && Array.isArray(children) ? "h5" : "small";
  const className = children && Array.isArray(children) ? styles["group-title"] : styles["group-item"];
  const identClassName = parent ? styles["has-parent"] : "";
  const selectedClassName = selected ? styles["is-selected"] : "";
  return /*#__PURE__*/React.createElement(Tag, {
    className: `${styles["option"]} ${className || ""} ${identClassName || ""} ${selectedClassName} ${index === 0 ? styles["first-child"] : ""}`,
    onClick: e => {
      e.persist();
      e.stopPropagation();
      e.preventDefault();
      onClick && onClick(e);
    }
  }, selected && /*#__PURE__*/React.createElement(Icon, {
    name: "checkmark"
  }), name);
};
const Options = ({
  value,
  filterEnabled,
  list,
  close,
  elem,
  onChange,
  i18n,
  scrollableDropdown,
  useI18n = true
}) => {
  const input = useRef(null);
  const [drawRect] = getRectStyle(elem);
  const [style, setStyle] = useState({
    ...drawRect,
    opacity: 0
  });
  const [selected, _] = useState(-1);
  const [filterValue, setFilterValue] = useState("");
  const listContainer = useRef(null);
  useEffect(() => {
    const onResize = () => {
      if (listContainer.current) {
        const rect = listContainer.current.getBoundingClientRect();
        const [newRect] = getRectStyle(elem);
        const bottomOverflow = newRect.top + rect.height > window.innerHeight - 10;
        if (rect.left + rect.width > window.innerWidth - 10) {
          newRect.left = window.innerWidth - 10 - rect.width;
        } else {
          newRect.left = rect.left;
        }
        if (bottomOverflow) {
          newRect.top = "auto";
          newRect.bottom = 10;
        }
        setStyle(newRect);
      } else {
        const [drawRect] = getRectStyle(elem);
        setStyle(drawRect);
      }
    };
    const handleClick = e => {
      e.stopPropagation();
      e.preventDefault();
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize);
    window.addEventListener("mousedown", handleClick, true);
    window.addEventListener("touchstart", handleClick, true);
    scrollIntoViewIfNeeded(selected, listContainer);
    return () => {
      window.removeEventListener("scroll", onResize);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousedown", handleClick, true);
      window.removeEventListener("touchstart", handleClick, true);
    };
  }, [selected, elem, list]);
  useEffect(() => {
    if (input && input.current) {
      input.current.focus();
    }
  }, [input]);
  useEffect(() => {
    let timeout = null;
    if (listContainer.current) {
      timeout = setTimeout(() => {
        const rect = listContainer.current.getBoundingClientRect();
        const [newRect, boundingRect] = getRectStyle(elem);
        if (rect.left + rect.width > window.innerWidth - 10) {
          newRect.left = "auto";
          newRect.right = window.innerWidth - (boundingRect.left + boundingRect.width);
        }
        if (rect.top + rect.height > window.innerHeight - 10) {
          newRect.top = "auto";
          newRect.bottom = 10;
        }
        setStyle({
          ...newRect,
          opacity: 1
        });
      }, 10);
    }
    setStyle({
      ...style,
      opacity: 0
    });
    return () => {
      clearTimeout(timeout);
    };
  }, [listContainer, elem]);
  let length = 0;
  let subLevel = (list || []).filter(item => item).some(item => item.options && item.options.length);
  const flatList = (list || []).filter(item => item).reduce((flatten, item, index) => {
    const listItem = {
      index,
      selected: item.id === value,
      name: useI18n ? i18n`${item.name}` : item.name,
      mandatory: item.mandatory,
      children: item.options && Array.isArray(item.options) ? [] : undefined,
      onClick: e => {
        e.stopPropagation();
        e.preventDefault();
        elem.focus();
        onChange && onChange({
          target: {
            value: item
          },
          originalEvent: e
        });
      }
    };
    flatten.push(listItem);
    length = Math.max(length, (item.name || "").length);
    if (item.options && item.options.length) {
      subLevel = true;
      item.options.forEach((subItem, subIndex) => {
        flatten.push({
          selected: item.id === value,
          index: [index, subIndex],
          name: subItem.name,
          parent: item.name,
          onClick: e => {
            e.stopPropagation();
            e.preventDefault();
            onChange && onChange({
              target: {
                value: subItem
              },
              originalEvent: e
            });
          }
        });
        if (!listItem.children) {
          listItem.children = [];
        }
        listItem.children.push(subItem.name);
        length = Math.max(length, (subItem.name || "").length);
      });
    }
    return flatten;
  }, []);
  const numberOfOptions = flatList.filter(item => !(item.children && item.children.length)).length;
  const normalizedFilterValue = filterValue && filterValue.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const filterRegex = new RegExp(`.*${normalizedFilterValue.replace(/\*/g, "\\*").replace(/\+/g, "\\+").replace(/\s/gi, ".*")}.*`, "ig");
  const filteredList = normalizedFilterValue ? flatList.filter(item => {
    if (subLevel) {
      if (item.children && item.children.length) {
        const normalizedName = item.name.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        return normalizedName.match(filterRegex) || item.children.some(child => {
          const normalizedChildName = child.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
          return normalizedChildName.match(filterRegex);
        });
      } else {
        const normalizedParentName = item.parent ? item.parent.normalize("NFD").replace(/[\u0300-\u036f]/g, "") : "";
        const normalizedName = item.name.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        return normalizedParentName.match(filterRegex) || normalizedName.match(filterRegex);
      }
    }
    const normalizedName = item.name.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    return normalizedName.match(filterRegex);
  }) : flatList;
  const listStyle = {
    minWidth: `${Math.max(20, parseInt(length * 0.8, 10))}ch`,
    maxWidth: "90vw"
  };
  const hasFilter = filterEnabled && numberOfOptions > 5;
  return /*#__PURE__*/React.createElement(Modal, null, /*#__PURE__*/React.createElement("div", {
    onClick: close,
    className: styles["backdrop"]
  }), /*#__PURE__*/React.createElement("div", {
    ref: listContainer,
    className: `${styles["options"]} ${hasFilter ? styles["filter-enabled"] : ""} ${scrollableDropdown ? styles["options-scroll"] : ""}`,
    style: style
  }, hasFilter && /*#__PURE__*/React.createElement("div", {
    className: styles["input-container"]
  }, /*#__PURE__*/React.createElement("input", {
    ref: input,
    value: filterValue,
    placeholder: i18n`Search...`,
    onChange: e => {
      setFilterValue(e.target.value);
    }
  })), filteredList && filteredList.length ? /*#__PURE__*/React.createElement(List, {
    i18n: i18n,
    className: styles["list"],
    items: filteredList,
    card: Item,
    modifiers: ["--list-margin-collapse"],
    style: listStyle
  }) : /*#__PURE__*/React.createElement("div", {
    className: styles["options-placeholder"],
    style: listStyle
  }, /*#__PURE__*/React.createElement("small", null, i18n`No data`))));
};
const Dropdown = props => {
  const {
    value,
    placeholder,
    label,
    options,
    className,
    filter,
    onClick,
    onChange,
    i18n: propsI18n,
    required,
    primary,
    secondary,
    highlightSelection,
    scrollableDropdown,
    useI18n = true,
    ...attributes
  } = props;
  const {
    i18n: globalI18n
  } = useGlobalValue();
  const [open, setOpen] = useState(false);
  const elem = useRef(null);
  const i18n = globalI18n || propsI18n;
  const inputValue = value && value.parent && value.parent.name || value && value.name || value;
  const inputValueId = value && value.parent && value.parent.id || value && value.id || value;
  return /*#__PURE__*/React.createElement("div", {
    className: `${styles["container"]} ${className || ""}`
  }, required && /*#__PURE__*/React.createElement("input", {
    className: styles["validation-input"],
    value: "",
    onChange: () => null,
    required: !inputValue || inputValue === ""
  }), /*#__PURE__*/React.createElement("button", _extends({
    type: "button",
    className: `
					dropdown
					${styles["button"]} ${primary ? styles["primary"] : ""} ${secondary ? styles["secondary"] : ""}
					${highlightSelection && inputValueId ? styles["is-selected"] : ""}
					${open ? styles["focus"] : ""}
				`,
    ref: elem
  }, attributes, {
    onClick: e => {
      onClick && onClick(e);
      setOpen(!open);
    }
  }), inputValue && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h5", {
    className: styles["title"]
  }, useI18n ? i18n`${inputValue}` : inputValue), /*#__PURE__*/React.createElement("small", {
    className: styles["subtitle"]
  }, value && value.parent && value.name || value && value.subtitle || label)), !(inputValue && inputValue.length) && placeholder !== undefined ? /*#__PURE__*/React.createElement("small", {
    className: styles["subtitle"]
  }, placeholder.length ? placeholder : i18n`-- Select an option --`) : null, /*#__PURE__*/React.createElement(Chevron, {
    className: styles["arrow"],
    direction: "down"
  })), open && /*#__PURE__*/React.createElement(Options, {
    value: inputValueId,
    onChange: e => {
      setOpen(false);
      onChange && onChange(e);
    },
    close: () => {
      setOpen(false);
    },
    elem: elem.current,
    filterEnabled: filter,
    list: options,
    i18n: i18n,
    useI18n: useI18n,
    scrollableDropdown: scrollableDropdown
  }));
};
export default Dropdown;